/** Form controls. Errors sit next to the field, in words, with the fix (WCAG 3.3.1, 3.3.3). */
import type { ComponentChildren, JSX, Ref } from 'preact';
import { useEffect, useId, useRef, useState } from 'preact/hooks';
import { Icon } from '../icons.tsx';

interface FieldShell {
  label: ComponentChildren;
  hint?: ComponentChildren;
  error?: string | null;
  id?: string;
}

function useFieldIds(id?: string) {
  const auto = useId();
  const base = id ?? `f${auto.replace(/[^a-zA-Z0-9]/g, '')}`;
  return { id: base, hint: `${base}-hint`, error: `${base}-err` };
}

function describe(ids: { hint: string; error: string }, hint: unknown, error: unknown): string | undefined {
  const parts = [hint ? ids.hint : null, error ? ids.error : null].filter(Boolean);
  return parts.length ? parts.join(' ') : undefined;
}

function FieldError({ id, error }: { id: string; error?: string | null }) {
  if (!error) return null;
  return (
    <p class="field-error" id={id}>
      <Icon name="alert" size={18} />
      <span>{error}</span>
    </p>
  );
}

export interface TextFieldProps extends FieldShell {
  value: string;
  onInput: (v: string) => void;
  type?: 'text' | 'email' | 'tel' | 'url' | 'date' | 'time' | 'search' | 'number';
  inputMode?: 'text' | 'email' | 'tel' | 'numeric' | 'decimal' | 'search' | 'url' | 'none';
  autoComplete?: string;
  placeholder?: string;
  required?: boolean;
  mono?: boolean;
  maxLength?: number;
  min?: string;
  max?: string;
  name?: string;
  inputRef?: Ref<HTMLInputElement>;
  onBlur?: () => void;
  enterKeyHint?: 'done' | 'go' | 'next' | 'search' | 'send';
  autoCapitalize?: 'off' | 'none' | 'sentences' | 'words';
  spellcheck?: boolean;
}

export function TextField(p: TextFieldProps): JSX.Element {
  const ids = useFieldIds(p.id);
  return (
    <div class="field">
      <label class="field-label" for={ids.id}>
        {p.label}
      </label>
      {p.hint && (
        <p class="field-hint" id={ids.hint}>
          {p.hint}
        </p>
      )}
      <input
        ref={p.inputRef}
        id={ids.id}
        name={p.name}
        class="input"
        data-mono={p.mono ? '' : undefined}
        type={p.type ?? 'text'}
        inputMode={p.inputMode}
        autoComplete={p.autoComplete}
        autoCapitalize={p.autoCapitalize}
        spellcheck={p.spellcheck}
        enterKeyHint={p.enterKeyHint}
        placeholder={p.placeholder}
        required={p.required}
        maxLength={p.maxLength}
        min={p.min}
        max={p.max}
        value={p.value}
        aria-invalid={p.error ? true : undefined}
        aria-describedby={describe(ids, p.hint, p.error)}
        onInput={(e) => p.onInput((e.currentTarget as HTMLInputElement).value)}
        onBlur={p.onBlur}
      />
      <FieldError id={ids.error} error={p.error} />
    </div>
  );
}

export function TextArea(p: FieldShell & { value: string; onInput: (v: string) => void; placeholder?: string; maxLength?: number; rows?: number; required?: boolean; inputRef?: Ref<HTMLTextAreaElement>; after?: ComponentChildren }): JSX.Element {
  const ids = useFieldIds(p.id);
  return (
    <div class="field">
      <label class="field-label" for={ids.id}>
        {p.label}
      </label>
      {p.hint && (
        <p class="field-hint" id={ids.hint}>
          {p.hint}
        </p>
      )}
      <textarea
        ref={p.inputRef}
        id={ids.id}
        class="textarea"
        rows={p.rows ?? 4}
        placeholder={p.placeholder}
        maxLength={p.maxLength}
        required={p.required}
        value={p.value}
        aria-invalid={p.error ? true : undefined}
        aria-describedby={describe(ids, p.hint, p.error)}
        onInput={(e) => p.onInput((e.currentTarget as HTMLTextAreaElement).value)}
      />
      {p.after}
      <FieldError id={ids.error} error={p.error} />
    </div>
  );
}

export function SelectField<T extends string>(p: FieldShell & { value: T | ''; onChange: (v: T | '') => void; options: { value: T; label: string }[]; placeholder?: string }): JSX.Element {
  const ids = useFieldIds(p.id);
  return (
    <div class="field">
      <label class="field-label" for={ids.id}>
        {p.label}
      </label>
      {p.hint && (
        <p class="field-hint" id={ids.hint}>
          {p.hint}
        </p>
      )}
      <select
        id={ids.id}
        class="select"
        value={p.value}
        aria-invalid={p.error ? true : undefined}
        aria-describedby={describe(ids, p.hint, p.error)}
        onChange={(e) => p.onChange((e.currentTarget as HTMLSelectElement).value as T | '')}
      >
        {p.placeholder !== undefined && <option value="">{p.placeholder}</option>}
        {p.options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <FieldError id={ids.error} error={p.error} />
    </div>
  );
}

export function Checkbox(p: { checked: boolean; onChange: (v: boolean) => void; children: ComponentChildren; error?: string | null; id?: string }): JSX.Element {
  const ids = useFieldIds(p.id);
  return (
    <div class="field">
      <label class="check" for={ids.id}>
        <input
          id={ids.id}
          type="checkbox"
          checked={p.checked}
          aria-invalid={p.error ? true : undefined}
          aria-describedby={p.error ? ids.error : undefined}
          onChange={(e) => p.onChange((e.currentTarget as HTMLInputElement).checked)}
        />
        <span>{p.children}</span>
      </label>
      <FieldError id={ids.error} error={p.error} />
    </div>
  );
}

export function ChoiceGroup<T extends string>(p: {
  legend: ComponentChildren;
  hint?: ComponentChildren;
  value: T | null;
  onChange: (v: T) => void;
  options: { value: T; label: ComponentChildren; detail?: ComponentChildren }[];
  error?: string | null;
  legendClass?: string;
}): JSX.Element {
  const ids = useFieldIds();
  return (
    <fieldset class="field" aria-describedby={describe(ids, p.hint, p.error)}>
      <legend class={p.legendClass ?? 'field-label'}>{p.legend}</legend>
      {p.hint && (
        <p class="field-hint" id={ids.hint}>
          {p.hint}
        </p>
      )}
      <div class="stack" data-gap="s">
        {p.options.map((o) => (
          <label class="choice" key={o.value}>
            <input type="radio" name={ids.id} value={o.value} checked={p.value === o.value} onChange={() => p.onChange(o.value)} />
            <span>
              <span class="choice-label">{o.label}</span>
              {o.detail && <span class="field-hint choice-detail">{o.detail}</span>}
            </span>
          </label>
        ))}
      </div>
      <FieldError id={ids.error} error={p.error} />
    </fieldset>
  );
}

/** A labelled switch (role=switch). The row is the target, so it stays 48 px tall. */
export function Switch(p: { checked: boolean; onChange: (v: boolean) => void; label: ComponentChildren; detail?: ComponentChildren; disabled?: boolean; lockedReason?: string | null; id?: string }): JSX.Element {
  const ids = useFieldIds(p.id);
  return (
    <div class="switch-row">
      <div>
        <span id={`${ids.id}-l`} class="switch-label">
          {p.label}
        </span>
        {(p.detail || p.lockedReason) && (
          <p class="field-hint" id={`${ids.id}-d`}>
            {p.lockedReason ? (
              <span class="locked">
                <Icon name="lock" size={16} /> {p.lockedReason}
              </span>
            ) : (
              p.detail
            )}
          </p>
        )}
      </div>
      <button
        type="button"
        role="switch"
        id={ids.id}
        class="switch"
        aria-checked={p.checked}
        aria-labelledby={`${ids.id}-l`}
        aria-describedby={p.detail || p.lockedReason ? `${ids.id}-d` : undefined}
        aria-disabled={p.disabled ? true : undefined}
        onClick={() => {
          if (!p.disabled) p.onChange(!p.checked);
        }}
      />
    </div>
  );
}

/**
 * Six code boxes (docs/52 S02): numeric keyboard, one-time-code autofill, paste fills all six, backspace walks
 * back. The first box carries the autocomplete hint so iOS and Android offer the emailed code.
 */
export function OtpInput(p: { value: string; onChange: (v: string) => void; onComplete?: (v: string) => void; label: string; digitLabel: (n: number) => string; error?: string | null; disabled?: boolean }): JSX.Element {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const ids = useFieldIds();
  const digits = Array.from({ length: 6 }, (_, i) => p.value[i] ?? '');
  const set = (next: string) => {
    const clean = next.replace(/\D/g, '').slice(0, 6);
    p.onChange(clean);
    if (clean.length === 6) p.onComplete?.(clean);
    return clean;
  };
  return (
    <fieldset class="field">
      <legend class="field-label">{p.label}</legend>
      <div class="otp" role="group" aria-describedby={p.error ? ids.error : undefined}>
        {digits.map((d, i) => (
          <input
            key={i}
            ref={(el) => {
              refs.current[i] = el;
            }}
            aria-label={p.digitLabel(i + 1)}
            inputMode="numeric"
            autoComplete={i === 0 ? 'one-time-code' : 'off'}
            pattern="[0-9]*"
            maxLength={i === 0 ? 6 : 1}
            value={d}
            disabled={p.disabled}
            aria-invalid={p.error ? true : undefined}
            onInput={(e) => {
              const el = e.currentTarget as HTMLInputElement;
              const typed = el.value.replace(/\D/g, '');
              if (typed.length > 1) {
                // Autofill or paste into one box: spread it.
                const clean = set(p.value.slice(0, i) + typed);
                refs.current[Math.min(clean.length, 5)]?.focus();
                return;
              }
              const arr = digits.slice();
              arr[i] = typed;
              const clean = set(arr.join(''));
              el.value = typed;
              if (typed && i < 5) refs.current[Math.min(clean.length, i + 1)]?.focus();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Backspace' && !digits[i] && i > 0) {
                e.preventDefault();
                const arr = digits.slice();
                arr[i - 1] = '';
                set(arr.join(''));
                refs.current[i - 1]?.focus();
              }
              if (e.key === 'ArrowLeft' && i > 0) refs.current[i - 1]?.focus();
              if (e.key === 'ArrowRight' && i < 5) refs.current[i + 1]?.focus();
            }}
            onPaste={(e) => {
              const text = e.clipboardData?.getData('text') ?? '';
              if (/\d/.test(text)) {
                e.preventDefault();
                const clean = set(text);
                refs.current[Math.min(clean.length, 5)]?.focus();
              }
            }}
          />
        ))}
      </div>
      <FieldError id={ids.error} error={p.error} />
    </fieldset>
  );
}

/** Mono text with a Copy button and a "Copied" live-region announcement (docs/52 §4.5, §4.6). */
export function RecordField(p: { label: string; value: string; copyLabel: string; copiedLabel: string }): JSX.Element {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);
  return (
    <div class="field">
      <span class="record-label">{p.label}</span>
      <div class="record" data-flash={copied ? '' : undefined}>
        <span class="record-value">{p.value}</span>
        <button
          type="button"
          class="btn"
          data-variant="secondary"
          data-size="sm"
          aria-label={`${p.copyLabel}: ${p.label}`}
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(p.value);
            } catch {
              // Clipboard can be blocked (http, permissions). Select the text so a long-press copy works.
              const sel = window.getSelection();
              const node = document.activeElement?.parentElement?.querySelector('.record-value');
              if (sel && node) {
                const r = document.createRange();
                r.selectNodeContents(node);
                sel.removeAllRanges();
                sel.addRange(r);
              }
            }
            setCopied(true);
          }}
        >
          <Icon name={copied ? 'check' : 'copy'} size={20} />
          <span>{copied ? p.copiedLabel : p.copyLabel}</span>
        </button>
      </div>
      <span class="sr-only" role="status" aria-live="polite">
        {copied ? p.copiedLabel : ''}
      </span>
    </div>
  );
}

/** Filter chips: a row of toggle buttons, one pressed. */
export function Filters<T extends string>(p: { label: string; value: T; onChange: (v: T) => void; options: { value: T; label: string; count?: number | null }[] }): JSX.Element {
  return (
    <div class="filters" role="group" aria-label={p.label}>
      {p.options.map((o) => (
        <button key={o.value} type="button" class="filter" aria-pressed={p.value === o.value} onClick={() => p.onChange(o.value)}>
          <span>{o.label}</span>
          {o.count != null && o.count > 0 && <span class="count">{o.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function Segmented<T extends string>(p: { label: string; value: T; onChange: (v: T) => void; options: { value: T; label: string }[] }): JSX.Element {
  return (
    <div class="segmented" role="group" aria-label={p.label}>
      {p.options.map((o) => (
        <button key={o.value} type="button" aria-pressed={p.value === o.value} onClick={() => p.onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}
